import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'

import type { ColumnLayout } from '@/api'
import {
  createColumnPreferenceStorage,
  parseColumnWidths,
  uniqueStringArray,
} from '@/utils/columnPreferenceStorage'

export const MIN_SOURCE_LIST_WIDTH = 224
export const MIN_SOURCE_DETAILS_WIDTH = 240
export const MAX_SOURCE_DETAILS_WIDTH = 480

interface SourceFilesLayout extends ColumnLayout {
  hidden: string[]
  widths: Record<string, number>
}

type PaneField = 'files' | 'details' | 'visibility'
interface PaneEdit {
  version: number
  value: number | boolean
}
interface PaneScope {
  userId: string
  projectId: number
  dirty: Map<PaneField, number>
  pending: Map<PaneField, PaneEdit>
}

function mergePaneEdits(
  layout: SourceFilesLayout | null,
  edits: ReadonlyMap<PaneField, PaneEdit>,
): SourceFilesLayout {
  return {
    order: ['files', 'pdf', 'details'],
    fixed: [],
    hidden: edits.has('visibility')
      ? edits.get('visibility')!.value
        ? []
        : ['details']
      : (layout?.hidden ?? []),
    widths: {
      files: Number(edits.get('files')?.value ?? layout?.widths.files ?? 320),
      details: Number(edits.get('details')?.value ?? layout?.widths.details ?? 288),
    },
  }
}

function parseLayout(raw: string): SourceFilesLayout | null {
  try {
    const candidate = JSON.parse(raw) as Partial<SourceFilesLayout>
    const hidden = uniqueStringArray(candidate.hidden)
    const widths = parseColumnWidths(candidate.widths)
    if (!hidden || !widths) return null
    return {
      order: ['files', 'pdf', 'details'],
      fixed: [],
      hidden: hidden.filter((key) => key === 'details'),
      widths,
    }
  } catch {
    return null
  }
}

export const useSourceFilesPaneStore = defineStore('sourceFilesPane', () => {
  const persistence = createColumnPreferenceStorage(
    'moria:source-files-layout:v1',
    parseLayout,
    (layout) =>
      layout ? { ...layout, hidden: [...layout.hidden], widths: { ...layout.widths } } : null,
    'source_files',
  )
  const scope = shallowRef<PaneScope | null>(null)
  const listWidth = ref(320)
  const detailsWidth = ref(288)
  const detailsOpen = ref(true)
  const pendingByScope = new Map<string, Map<PaneField, PaneEdit>>()
  let editVersion = 0
  let generation = 0

  function applyLayout(layout: SourceFilesLayout | null, preserveDirty = false): void {
    const dirty = preserveDirty ? scope.value?.dirty : null
    if (!dirty?.has('files')) {
      listWidth.value = Math.max(MIN_SOURCE_LIST_WIDTH, Math.min(5000, layout?.widths.files ?? 320))
    }
    if (!dirty?.has('details')) {
      detailsWidth.value = Math.max(
        MIN_SOURCE_DETAILS_WIDTH,
        Math.min(MAX_SOURCE_DETAILS_WIDTH, layout?.widths.details ?? 288),
      )
    }
    if (!dirty?.has('visibility')) detailsOpen.value = !layout?.hidden.includes('details')
  }

  async function initialize(userId: string | null, projectId: number): Promise<void> {
    if (scope.value?.userId === userId && scope.value?.projectId === projectId) return
    const key = `${userId}:${projectId}`
    if (userId && !pendingByScope.has(key)) pendingByScope.set(key, new Map())
    const target: PaneScope | null = userId
      ? { userId, projectId, dirty: new Map(), pending: pendingByScope.get(key)! }
      : null
    scope.value = target
    applyLayout(null)
    if (!target) return
    await persistence.hydrate(target.userId, target.projectId)
    if (scope.value === target) {
      applyLayout(persistence.load(target.userId, target.projectId).preference, true)
    }
  }

  function setListWidth(value: number): void {
    if (!Number.isFinite(value)) return
    listWidth.value = Math.max(MIN_SOURCE_LIST_WIDTH, Math.min(5000, Math.round(value)))
    if (scope.value) scope.value.dirty.set('files', ++editVersion)
  }

  function setDetailsWidth(value: number): void {
    if (!Number.isFinite(value)) return
    detailsWidth.value = Math.max(
      MIN_SOURCE_DETAILS_WIDTH,
      Math.min(MAX_SOURCE_DETAILS_WIDTH, Math.round(value)),
    )
    if (scope.value) scope.value.dirty.set('details', ++editVersion)
  }

  function setDetailsOpen(value: boolean): void {
    detailsOpen.value = value
    if (scope.value) scope.value.dirty.set('visibility', ++editVersion)
  }

  function finishSave(
    target: PaneScope,
    edits: ReadonlyMap<PaneField, PaneEdit>,
    saved: ReturnType<typeof persistence.load>,
  ): void {
    if (saved.status === 'loaded') {
      for (const [field, edit] of edits) {
        if (target.pending.get(field)?.version === edit.version) target.pending.delete(field)
        if (target.dirty.get(field) === edit.version) target.dirty.delete(field)
      }
    }
    if (scope.value?.userId === target.userId && scope.value?.projectId === target.projectId) {
      applyLayout(saved.preference, true)
    }
  }

  async function save(): Promise<void> {
    const target = scope.value
    if (!target || (!target.dirty.size && !target.pending.size)) return
    const epoch = generation
    const values: Record<PaneField, number | boolean> = {
      files: listWidth.value,
      details: detailsWidth.value,
      visibility: detailsOpen.value,
    }
    for (const [field, version] of target.dirty) {
      target.pending.set(field, { version, value: values[field] })
    }
    const edits = new Map(target.pending)
    const result = await persistence.update(target.userId, target.projectId, (_current, server) =>
      mergePaneEdits(server, target.pending),
    )
    if (epoch !== generation) return
    if (!result.persisted) {
      // Preferences are implicit: rebase a conflicting revision without interrupting the user.
      await persistence.retry(target.userId, target.projectId, (_local, remote) =>
        mergePaneEdits(remote, target.pending),
      )
    }
    if (epoch !== generation) return
    finishSave(target, edits, persistence.load(target.userId, target.projectId))
  }

  function clear(): void {
    persistence.clear()
    generation += 1
    pendingByScope.clear()
    editVersion = 0
    scope.value = null
    applyLayout(null)
  }

  return {
    listWidth,
    detailsWidth,
    detailsOpen,
    initialize,
    setListWidth,
    setDetailsWidth,
    setDetailsOpen,
    save,
    clear,
  }
})
